import { useCallback, useEffect, useRef } from 'react';

import { buildQuestionAnswerPayload, type SurveyAnswerState } from '../buildAnswerPayload';

import type { SurveyQuestionData } from '../types';

import { useSaveManagerAssessmentDraftAnswers } from '@/api/cycle-evaluation-services/manager-assessment';

interface UseDraftAnswerAutosaveParams {
    cycleInstanceId: string | undefined;
    isReadOnly: boolean;
    // Whether the survey has already been submitted at least once. Never gates the save itself — it
    // only tells the mutation whether the backend just recomputed Results, so it knows whether there's
    // anything new to invalidate (#15643).
    isSubmitted: boolean;
    questions: SurveyQuestionData[];
    answerState: SurveyAnswerState;
    /** The answers loaded from the server: already persisted, so never sent back unchanged (#16607). */
    loadedAnswers?: SurveyAnswerState;
    currentQuestionId: string | undefined;
}

/**
 * Persists the answer to the question the manager is on, so a refresh mid-survey doesn't lose it.
 *
 * Saving happens when they move off the question (and once more if the page unmounts) — never while they
 * type, so an "Other" text costs one request instead of one per keystroke. Writes go to the draft endpoint, which
 * upserts only the question it is given; submitting stays a separate, single call at the end. The payload
 * is deduplicated against the last one sent for that question, so paging back and forth over untouched
 * answers costs nothing.
 *
 * Autosave keeps running after the survey has been submitted (#15643): the draft endpoint then recomputes
 * the score, competency levels and AI artifacts from the edited answer the same way submit does, so a
 * revision propagates immediately instead of requiring the manager to submit a second time.
 */
export const useDraftAnswerAutosave = ({
    cycleInstanceId,
    isReadOnly,
    isSubmitted,
    questions,
    answerState,
    loadedAnswers,
    currentQuestionId,
}: UseDraftAnswerAutosaveParams) => {
    const { mutateAsync: saveDraftAnswers } = useSaveManagerAssessmentDraftAnswers();

    // Read at save time rather than closed over, so navigating always sends the newest values.
    const answerStateRef = useRef(answerState);
    answerStateRef.current = answerState;
    const questionsRef = useRef(questions);
    questionsRef.current = questions;
    const currentQuestionIdRef = useRef(currentQuestionId);
    currentQuestionIdRef.current = currentQuestionId;
    const isSubmittedRef = useRef(isSubmitted);
    isSubmittedRef.current = isSubmitted;

    const canSaveRef = useRef(false);
    canSaveRef.current = Boolean(cycleInstanceId) && !isReadOnly;

    // The payload last persisted per question, so an unchanged answer is never re-sent.
    const savedPayloadsRef = useRef<Record<string, string>>({});

    // Each step is its own route, so coming back to the survey starts from an empty map. Past submission
    // every draft save recomputes the results (AI summary, Mastered/Growth lists), so re-sending an
    // answer nobody changed would wipe them (#16607): the loaded answers count as already saved.
    useEffect(() => {
        if (!loadedAnswers) {
            return;
        }
        questionsRef.current.forEach((question) => {
            savedPayloadsRef.current[question.competencyId] ??= JSON.stringify(
                buildQuestionAnswerPayload(question, loadedAnswers),
            );
        });
    }, [loadedAnswers]);

    const saveCurrentAnswer = useCallback(() => {
        const questionId = currentQuestionIdRef.current;
        if (!canSaveRef.current || !cycleInstanceId || !questionId) {
            return;
        }

        const question = questionsRef.current.find(
            (surveyQuestion) => surveyQuestion.competencyId === questionId,
        );
        if (!question) {
            return;
        }

        const payload = buildQuestionAnswerPayload(question, answerStateRef.current);
        const serialized = JSON.stringify(payload);
        if (savedPayloadsRef.current[questionId] === serialized) {
            return;
        }
        savedPayloadsRef.current[questionId] = serialized;

        // A failed save is not surfaced: the answer stays in local state, moving off the question again
        // retries, and submit sends the full set regardless. Drop the snapshot so the retry isn't deduped.
        void saveDraftAnswers({
            cycleInstanceId,
            payload: { answers: [payload] },
            alreadySubmitted: isSubmittedRef.current,
        }).catch(() => {
            delete savedPayloadsRef.current[questionId];
        });
    }, [cycleInstanceId, saveDraftAnswers]);

    // Leaving the page mid-question still commits what's on screen.
    useEffect(() => saveCurrentAnswer, [saveCurrentAnswer]);

    return { saveCurrentAnswer };
};
